import { useMemo } from 'react';
import { ContentNode, Epic, Troubleshooting } from '@amzn/awscat-pattern-library-service-model';

import { listEpics } from '../../services/EpicOperations';
import { filterRenderableSections } from '../../services/SectionVisibility';
import { listTroubleshootings } from '../../services/TroubleshootingOperations';
import { useContent } from '../v2-hooks/useContent';
import { usePatternEditorViewModel } from '../v2-content-editor/PatternEditorViewModel';

/** Read-only presentation state for the complete Pattern body. */
export interface PatternReadonlyViewModel {
  /** Non-empty sections to render, in document order. */
  readonly sections: readonly ContentNode[];
  /** Content ID for image loading and comment anchoring. */
  readonly contentId: string;
}

/** Read-only presentation state for the Pattern Epics section. */
export interface PatternReadonlyEpicsViewModel {
  /** Epics to render, in document order; empty when none or no content is loaded. */
  readonly epics: readonly Epic[];
}

/** Read-only presentation state for the Pattern Troubleshooting section. */
export interface PatternReadonlyTroubleshootingViewModel {
  /** Troubleshooting entries to render; empty when none or no content is loaded. */
  readonly rows: readonly Troubleshooting[];
}

/** Projects the Pattern editor state into the non-empty read-only body. */
export function usePatternReadonlyViewModel(): PatternReadonlyViewModel | undefined {
  const editorViewModel = usePatternEditorViewModel();
  const sections = useMemo(
    () => filterRenderableSections(editorViewModel?.sections ?? []),
    [editorViewModel?.sections],
  );

  // The underlying editor view model returns undefined during teardown; mirror
  // that here so the view renders null instead of dereferencing undefined.
  if (!editorViewModel) return undefined;

  return { sections, contentId: editorViewModel.contentId };
}

/** Projects the loaded content's epics into the read-only Epics section. */
export function usePatternReadonlyEpicsViewModel(): PatternReadonlyEpicsViewModel {
  const content = useContent();
  const epics = useMemo(() => (content ? listEpics(content) : []), [content]);
  return { epics };
}

/** Projects the loaded content's troubleshooting entries into the read-only Troubleshooting section. */
export function usePatternReadonlyTroubleshootingViewModel(): PatternReadonlyTroubleshootingViewModel {
  const content = useContent();
  const rows = useMemo(() => (content ? listTroubleshootings(content) : []), [content]);
  return { rows };
}
